import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Clock, Minus } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { buildSummary, type CatalogEntry, type LinkForm, type LinkSummaryData, type SummaryCtx, type SummaryLine } from './link-form';

/** Date for the summary lines, e.g. "Fri 31 Oct, 14:30", in the interface language. */
export function useSummaryFormatters(): SummaryCtx['fmt'] {
  const { t, i18n } = useTranslation('mobileLinks');
  return useMemo(
    () => ({
      money: (n: number) => formatCurrency(n),
      date: (d: Date) => d.toLocaleString(i18n.language || undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
      rel: (ms: number) => {
        const hours = Math.max(1, Math.round(ms / 3_600_000));
        return hours < 48 ? t('relHours', { n: hours }) : t('relDays', { n: Math.round(hours / 24) });
      },
    }),
    [i18n.language, t],
  );
}

/** Summary data for a form (create dialog) or a stored link (drawer, link-ready dialog). */
export function useLinkSummary(form: LinkForm, catalog: Pick<CatalogEntry, 'key'>[], expiresAt: Date | null, locationName?: string | null): LinkSummaryData {
  const fmt = useSummaryFormatters();
  const expiresMs = expiresAt?.getTime() ?? null;
  return useMemo(
    () => buildSummary(form, catalog, { fmt, expiresAt: expiresMs === null ? null : new Date(expiresMs), locationName }),
    [form, catalog, fmt, expiresMs, locationName],
  );
}

/** Flat text of the summary, used for the debounced screen-reader announcement. */
export function summaryAnnouncement(t: (k: string, o?: Record<string, unknown>) => string, s: LinkSummaryData): string {
  return `${t('summaryBar', { can: s.can.length, cannot: s.cannot.length, limits: s.limits.length })}. ${s.can.map((l) => t(l.key, l.params)).join('; ')}`;
}

function Group({ title, lines, icon, tone }: { title: string; lines: SummaryLine[]; icon: 'can' | 'cannot' | 'limit'; tone: string }) {
  const { t } = useTranslation('mobileLinks');
  const Icon = icon === 'can' ? Check : icon === 'cannot' ? Minus : Clock;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {lines.length === 0 ? (
        <p className="mt-1 text-sm text-muted-foreground">-</p>
      ) : (
        <ul className="mt-1 space-y-1.5">
          {lines.map((l, i) => (
            <li key={`${l.key}-${i}`} className="flex items-start gap-2 text-sm">
              <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone)} aria-hidden />
              <span className="min-w-0 break-words">{t(l.key, l.params)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The "This link can / cannot / limits" renderer, shared by the create dialog, the link-ready dialog and the details drawer. */
export function LinkSummary({ summary, className }: { summary: LinkSummaryData; className?: string }) {
  const { t } = useTranslation('mobileLinks');
  return (
    <div className={cn('space-y-4', className)}>
      <Group title={t('sumCan')} lines={summary.can} icon="can" tone="text-success-text" />
      <Group title={t('sumCannot')} lines={summary.cannot} icon="cannot" tone="text-muted-foreground" />
      <Group title={t('sumLimits')} lines={summary.limits} icon="limit" tone="text-info-text" />
    </div>
  );
}
